import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueryClient } from 'react-query';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { type LocationServiceLocation, LocationPickerTray } from '@frontend/wam-location-service';
import {
  Chip,
  InlineNotification,
  Modal,
  NakedButton,
  SpinningLoader,
  Text,
  Tray,
  useModalControl,
} from '@frontend/design-system';
import { useTrueLarkLocation } from '../hooks/use-truelark-location';
import {
  useSubscriptionStatus,
  useActiveBusinessPlan,
  queryKeys,
  useBusinessDetails,
  subscriptionChipVariant,
  type TrueLarkDomains,
} from '../index';
import { truelarkStyles as styles } from '../styles';
import { BusinessDetailsCard } from './business-details-card';
import { UpdateBusinessPlanModal } from './update-plan-modal';
import { UpdateSubscriptionModal } from './update-subscription-modal';

type Props = {
  user: { userID: string; username: string } | undefined;
  configCard: React.ComponentType<{ title: string; onEdit?: () => void; children: React.ReactNode }>;
};

export function TrueLarkConfigurations({ user, configCard: ConfigCard }: Props) {
  const queryClient = useQueryClient();
  const { modalProps: editStatusModalProps, openModal: openEditStatusModal } = useModalControl();
  const { modalProps: editPlanModalProps, openModal: openEditPlanModal } = useModalControl();
  const { selectedBusiness, locationTrayProps, locationTriggerProps, searchLocations, onChangeLocation, itemSections } =
    useTrueLarkLocation(user?.userID);
  const canUpdateTrueLark = hasSchemaACL('weave', Permission.TRUELARK_WAM_WRITE);

  const {
    data: subscriptionData,
    isLoading: isLoadingSubscription,
    isError: isErrorSubscription,
  } = useSubscriptionStatus(selectedBusiness?.locationId);

  const {
    data: activeBusinessPlanData,
    isLoading: isLoadingActiveBusinessPlan,
    isError: isErrorActiveBusinessPlan,
  } = useActiveBusinessPlan(selectedBusiness?.locationId);

  const {
    data: businessDetailsData,
    isLoading: isLoadingBusinessDetails,
    isError: isErrorBusinessDetails,
  } = useBusinessDetails(selectedBusiness?.locationId);

  return (
    <>
      <div css={styles.pageContent}>
        <div>
          <NakedButton css={styles.locationButton} {...locationTriggerProps}>
            <Icon name='location' size={16} />
            <Text size='medium'>{selectedBusiness?.name ?? 'Select Location'}</Text>
            <Icon name='caret-down' size={16} />
          </NakedButton>
          <Tray {...locationTrayProps} width='large' mountTarget='#tray-target'>
            <LocationPickerTray
              currentLocation={selectedBusiness as LocationServiceLocation | null}
              itemSections={itemSections}
              onClose={locationTrayProps.onClose}
              onChangeLocation={onChangeLocation}
              searchLocations={searchLocations}
            />
          </Tray>
        </div>

        <div css={styles.cardsGrid}>
          <ConfigCard
            title='Subscription Status'
            onEdit={selectedBusiness && canUpdateTrueLark ? openEditStatusModal : undefined}
          >
            {!selectedBusiness ? (
              <Text color='subdued'>Select a location to view subscription status</Text>
            ) : isLoadingSubscription ? (
              <SpinningLoader size='small' />
            ) : isErrorSubscription ? (
              <InlineNotification status='error' message='Failed to load subscription status' />
            ) : subscriptionData?.subscriptionStatus ? (
              <Chip variant={subscriptionChipVariant(subscriptionData.subscriptionStatus)}>
                {subscriptionData.subscriptionStatus}
              </Chip>
            ) : (
              <Text color='subdued'>No subscription status available</Text>
            )}
          </ConfigCard>

          <ConfigCard
            title='Business Plan'
            onEdit={selectedBusiness && canUpdateTrueLark ? openEditPlanModal : undefined}
          >
            {!selectedBusiness ? (
              <Text color='subdued'>Select a location to view plan</Text>
            ) : isLoadingActiveBusinessPlan ? (
              <SpinningLoader size='small' />
            ) : isErrorActiveBusinessPlan ? (
              <InlineNotification status='error' message='Failed to load plan info' />
            ) : activeBusinessPlanData?.plan ? (
              <Text color='subdued'>{activeBusinessPlanData.plan.id}</Text>
            ) : (
              <Text color='subdued'>No status available</Text>
            )}
          </ConfigCard>

          <div css={styles.fullWidthCell}>
            <ConfigCard title='Business Details'>
              <BusinessDetailsCard
                data={businessDetailsData}
                isLoading={isLoadingBusinessDetails}
                isError={isErrorBusinessDetails}
              />
            </ConfigCard>
          </div>
        </div>
      </div>

      {selectedBusiness && canUpdateTrueLark && (
        <Modal {...editStatusModalProps} maxWidth={480}>
          <UpdateSubscriptionModal
            locationId={selectedBusiness.locationId}
            currentStatus={subscriptionData?.subscriptionStatus ?? ''}
            updatedBy={user?.username ?? ''}
            onClose={editStatusModalProps.onClose}
            onSuccess={() => queryClient.invalidateQueries(queryKeys.subscriptionStatus(selectedBusiness.locationId))}
          />
        </Modal>
      )}
      {selectedBusiness && canUpdateTrueLark && (
        <Modal {...editPlanModalProps} maxWidth={480}>
          <UpdateBusinessPlanModal
            domain={selectedBusiness.domain as TrueLarkDomains}
            client_email={businessDetailsData?.email ?? ''}
            currentPlan={activeBusinessPlanData?.plan?.id ?? ''}
            updatedBy={user?.username ?? ''}
            onClose={editPlanModalProps.onClose}
            onSuccess={() => queryClient.invalidateQueries(queryKeys.businessPlanDetails(selectedBusiness.locationId))}
          />
        </Modal>
      )}
    </>
  );
}
